import { AxiosError } from 'axios';

import { logger } from '../logger';

export enum ErrorTypes {
    NotFound = '999899',
    Connection = '999898',
    General = '999897',
    ServerError = '999896',
    Timeout = '999895',
    DeprecatedOrderData = '100052',
    CandidateOffersNotFound = '100066',
    EmptyOrderId = '100009',
    OrderNotFound = '100001',
    OrderRoleNotFound = '100078',
    OrderNoLongerAvailable = '100094'
}

export type ErrorCode = string | ErrorTypes;

export enum ErrorSeverity {
    warn = 'warn',
    error = 'error'
}

export interface Error {
    errorCode: string;
    errorSeverity: ErrorSeverity;
    /**
     * @deprecated message should not be used. For user faced messages use `useErrorText` instead
     */
    message?: string | Record<string, unknown>;
}

export interface BEError {
    appInsightsOperationId: string;
    developerMessage: string;
    errorCode: string;
}

export interface BEErrorHandlerInterface {
    error: AxiosError;
    applicable(): boolean;
    handle(): ApiError;
}

export class ApiError implements Error {

    errorCode: string;
    errorSeverity: ErrorSeverity;
    message: string;

    constructor(errorSeverity: ErrorSeverity, message: string, errorCode: string) {
        this.errorCode = errorCode;
        this.errorSeverity = errorSeverity;
        this.message = message;
    }

}

export class BEErrorHandler implements BEErrorHandlerInterface {

    error: AxiosError;

    constructor(error: AxiosError) {
        this.error = error;
    }

    applicable() {
        return true;
    }

    handle() {
        const { request, response } = this.error;

        if (response) {

            if (response.headers?.['content-type'] === 'application/json') {

                // Error might be received as ArrayBuffer if
                // request options contains { responseType: 'arraybuffer' }
                // so we need to convert such result to string and then to object
                const textDecoder = new TextDecoder();
                const errorData = response.data instanceof ArrayBuffer
                    ? JSON.parse(textDecoder.decode(response.data))
                    : response.data;

                const data = errorData as BEError;
                const {
                    appInsightsOperationId,
                    developerMessage
                } = data;

                let errorCode = data.errorCode;

                if (errorCode === null) {
                    errorCode = '';
                }

                logger.error(`\nAPI Error ${ response.status }\n${ developerMessage }\nAppInsights operationId: ${ appInsightsOperationId }`);

                if (response.status === 404) {
                    return new ApiError(ErrorSeverity.warn, '', ErrorTypes.NotFound);
                }

                if (response.status >= 500) {
                    return new ApiError(ErrorSeverity.error, developerMessage, ErrorTypes.ServerError);
                }

                return new ApiError(ErrorSeverity.error, '', errorCode || ErrorTypes.General);
            }

            /*
             * The request was made and the server responded with a
             * status code that falls out of the interval of 2xx
             */
            const { data, statusText } = response;
            const developerMessage = (data as BEError)?.developerMessage;
            const errorMessage = developerMessage ? developerMessage : statusText;

            logger.error(`\nAPI Error ${ response.status }\nError message: ${ errorMessage }`);

            if (response.status === 404) {
                return new ApiError(ErrorSeverity.warn, errorMessage, ErrorTypes.NotFound);
            }

            if (response.status >= 500) {
                return new ApiError(ErrorSeverity.error, errorMessage, ErrorTypes.ServerError);
            }

            return new ApiError(ErrorSeverity.error, errorMessage, ErrorTypes.General);
        } else if (request) {
            /*
             * The request was made but no response was received, `error.request`
             * is an instance of XMLHttpRequest in the browser and an instance
             * of http.ClientRequest in Node.js
             */
            logger.error(`\nAPI Error\nCould not get response from server`);

            return new ApiError(
                ErrorSeverity.error,
                'Cannot establish connection to the server. Please check your network and try again',
                ErrorTypes.Timeout
            );
        } else {
            logger.error(`\nAPI Error\nNo response and no request object in this error. Something went wrong, probably connection lost`);

            return new ApiError(
                ErrorSeverity.error,
                'Cannot establish connection to the server. Please check your network and try again',
                ErrorTypes.Connection
            );
        }
    }

}
